/**
 * HttpSettingsFields — the 34-field HTTP camera settings body, extracted from
 * CameraHttpSettingsSheet (item 226) so a group bulk-settings dialog can
 * render the identical fields against shared form state.
 *
 * Moved verbatim from CameraHttpSettingsSheet.tsx: `FieldRow`, `SectionHeading`,
 * every option constant, `intRange`, and the whole scrollable body from the
 * Capture section through the System section. Every read of a state variable
 * `x` is now `form.x`; every `setX(v)` is now `set('x', v)`; every
 * `pendingFields.has(...)` is `pendingFields?.has(...) ?? false` (single-camera
 * only — absent in a bulk dialog); every bucket-1 `reported=` comes from the
 * `reported` prop, computed by the caller exactly as before.
 */

import { createContext, useContext, type ComponentProps } from 'react'
import { AlertTriangle, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Input as BaseInput } from '@/components/ui/input'
import { Select as BaseSelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { DesiredExtraSettings } from '@/types'
import { UNCHANGED, type HttpSettingsFormState } from '@/lib/httpSettingsForm'

// ─────────────────────────────────────────────────────────────────────
// Camera setting enums (mirrored from backend services/boly_enums.py)
// ─────────────────────────────────────────────────────────────────────

const PIR_INTERVAL_OPTIONS: Array<{ value: number; label: string }> = [
  ...[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((n) => ({ value: n, label: `${n} sec` })),
  ...[1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map((m) => ({
    value: m * 60,
    label: `${m} min`,
  })),
]
// Photo / video / PIR-trigger option sets aligned to the Document C spec
// shipped with V1.04.06030-T firmware (2026-05-13). Previous BG662M
// (V1.04.06028) options ('12'/'14'/'36'/'48' MP, etc.) never actually
// applied because the legacy #E#…# settings parser was broken — kept in git
// history for reference but removed from the UI since they'd be encoded
// to no-op values by the menu_txt_encoder. See change proposal #042.
const PHOTO_SIZE_OPTIONS = [
  { value: '8M', label: '8 MP' },
  { value: '10M', label: '10 MP' },
  { value: '32M', label: '32 MP' },
  { value: '56M', label: '56 MP' },
]
const VIDEO_RESOLUTION_OPTIONS = [
  { value: '3840x2160', label: '3840×2160 (4K)' },
  { value: '2688x1520', label: '2688×1520 (2.7K)' },
  { value: '1920x1080', label: '1920×1080 (1080p)' },
  { value: '1280x720', label: '1280×720 (720p)' },
  { value: '640x480', label: '640×480 (VGA)' },
  { value: '320x240', label: '320×240 (QVGA)' },
]
const VIDEO_LENGTH_OPTIONS = [1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 90, 120].map((n) => ({
  value: n,
  label: `${n} sec`,
}))
const PIR_TRIGGER_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
]
const CAMERA_MODE_OPTIONS = [
  { value: 'photo', label: 'Photo' },
  { value: 'video', label: 'Video' },
  { value: 'both', label: 'Photo + Video' },
]
const ATTACHMENT_SIZE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
]
const TIME_LAPSE_MIN_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
const TIME_LAPSE_HR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8]
const ON_OFF_OPTIONS = [
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
]
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// ── #044 Phase B: full Document C settings option sets ──
const DATE_TYPE_OPTIONS = [
  { value: 'ymd', label: 'Y-M-D' },
  { value: 'mdy', label: 'M-D-Y' },
  { value: 'dmy', label: 'D-M-Y' },
]
const AUTO_SIM_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'manual', label: 'Manual' },
]
const LOCK_NET_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: '2g', label: '2G' },
  { value: '3g', label: '3G' },
  { value: '4g', label: '4G' },
]
const ALARM_FLASH_OPTIONS = [
  ...Array.from({ length: 12 }, (_, i) => ({ value: `BR${i + 1}`, label: `Burst ${i + 1}` })),
  ...[2, 3, 4, 5].map((n) => ({ value: `BRDOUBLE${n}`, label: `Burst-double ${n}` })),
  ...Array.from({ length: 6 }, (_, i) => ({ value: `WHITE${i + 1}`, label: `White ${i + 1}` })),
]
const LANGUAGE_OPTIONS = [
  'ENGLISH', 'FINLAND', 'GERMANY', 'FRENCH', 'SPAIN', 'SWEDISH', 'FINNISH',
  'NORWAY', 'DANISH', 'ITALIAN', 'POLISH', 'RUSSIAN', 'JAPANESE',
].map((v) => ({ value: v, label: v[0] + v.slice(1).toLowerCase() }))
const HEARTBEAT_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'Every 6h' },
  { value: 2, label: 'Every 12h' },
  { value: 3, label: 'Every 24h' },
]
// Inclusive numeric ranges → dropdown options.
const intRange = (lo: number, hi: number) =>
  Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)

// ─────────────────────────────────────────────────────────────────────
// FieldRow — section row layout for one labelled control
// ─────────────────────────────────────────────────────────────────────

// #410 — a read-only row (owner-only plumbing for a sharee with the settings tick) or a read-only form (a view-only
// sharee). Each control gets its own `disabled`: Radix Select opens on its own prop only, not on an enclosing
// <fieldset disabled>, and some browsers still dispatch pointer events to a control disabled by its fieldset.
const LockedContext = createContext(false)

function Select(props: ComponentProps<typeof BaseSelect>) {
  const locked = useContext(LockedContext)
  return <BaseSelect {...props} disabled={locked || props.disabled} />
}

function Input(props: ComponentProps<typeof BaseInput>) {
  const locked = useContext(LockedContext)
  return <BaseInput {...props} disabled={locked || props.disabled} />
}

function FieldRow({
  label,
  hint,
  reported,
  pending,
  sent,
  locked,
  children,
}: {
  label: string
  hint?: string
  reported?: string
  pending?: boolean
  sent?: boolean   // #396 — pending AND already handed to the camera (blue, not amber)
  locked?: boolean // #410 — shown but not editable (owner-only plumbing for a person with the settings tick)
  children: React.ReactNode
}) {
  const { t } = useTranslation('cameras')
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-border/50 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        {reported && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Camera reports: <span className="font-mono">{reported}</span>
          </p>
        )}
        {pending && sent && (
          <p className="text-xs text-sky-600 dark:text-sky-400 mt-0.5 flex items-center gap-1">
            <Send className="h-3 w-3 shrink-0" /> Sent, awaiting confirmation
          </p>
        )}
        {pending && !sent && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 shrink-0" /> Not applied yet
          </p>
        )}
        {locked && <p className="text-xs text-muted-foreground mt-0.5">{t('edit.ownerOnlyField')}</p>}
      </div>
      <div className="shrink-0 min-w-[180px] max-w-[260px]">
        {locked
          ? <LockedContext.Provider value><fieldset disabled className="contents">{children}</fieldset></LockedContext.Provider>
          : children}
      </div>
    </div>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mt-2 mb-1">
      {children}
    </h3>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────

export interface HttpSettingsFieldsProps {
  form: HttpSettingsFormState
  set: <K extends keyof HttpSettingsFormState>(key: K, value: HttpSettingsFormState[K]) => void
  pendingFields?: Set<string>          // single-camera only — every unconfirmed field
  sentFields?: Set<string>             // single-camera only — #396 the subset already handed to the camera
  lockPlumbing?: boolean               // #410 — the seven plumbing rows are read-only (settings tick without ownership)
  disabled?: boolean                   // #410 — every row is read-only (a view-only sharee)
  reported?: {                          // single-camera only (bucket-1 "Camera reports")
    pirInterval?: string; photoSize?: string; videoResolution?: string; videoLength?: string
  }
}

export function HttpSettingsFields({ form, set, pendingFields, sentFields, lockPlumbing, disabled = false, reported }: HttpSettingsFieldsProps) {
  // Narrowed once here so the count Input's onChange closure is type-safe
  // (TS doesn't preserve discriminated-union narrowing into closures).
  const sendModeObj =
    form.sendMode !== UNCHANGED && form.sendMode.mode !== 'off' ? form.sendMode : null

  return (
    <LockedContext.Provider value={disabled}>
      {/* ─── Capture (bucket-1) ─── */}
      <section>
        <SectionHeading>Capture</SectionHeading>
        <FieldRow label="PIR interval" hint="Wait between motion triggers" reported={reported?.pirInterval} pending={pendingFields?.has('desired_pir_interval') ?? false} sent={sentFields?.has('desired_pir_interval') ?? false}>
          <Select
            value={form.pirInterval === UNCHANGED ? UNCHANGED : String(form.pirInterval)}
            onValueChange={(v) => set('pirInterval', v === UNCHANGED ? UNCHANGED : Number(v))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {PIR_INTERVAL_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Photo size" reported={reported?.photoSize} pending={pendingFields?.has('desired_photo_size') ?? false} sent={sentFields?.has('desired_photo_size') ?? false}>
          <Select value={form.photoSize === UNCHANGED ? UNCHANGED : form.photoSize} onValueChange={(v) => set('photoSize', v === UNCHANGED ? UNCHANGED : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {PHOTO_SIZE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Video resolution" reported={reported?.videoResolution} pending={pendingFields?.has('desired_video_resolution') ?? false} sent={sentFields?.has('desired_video_resolution') ?? false}>
          <Select value={form.videoResolution === UNCHANGED ? UNCHANGED : form.videoResolution} onValueChange={(v) => set('videoResolution', v === UNCHANGED ? UNCHANGED : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {VIDEO_RESOLUTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Video length" reported={reported?.videoLength} pending={pendingFields?.has('desired_video_length') ?? false} sent={sentFields?.has('desired_video_length') ?? false}>
          <Select value={form.videoLength === UNCHANGED ? UNCHANGED : String(form.videoLength)} onValueChange={(v) => set('videoLength', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {VIDEO_LENGTH_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Camera mode" pending={pendingFields?.has('desired_extra_settings.camera_mode') ?? false} sent={sentFields?.has('desired_extra_settings.camera_mode') ?? false}>
          <Select value={form.cameraMode === UNCHANGED ? UNCHANGED : form.cameraMode!} onValueChange={(v) => set('cameraMode', v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['camera_mode']))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {CAMERA_MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

      </section>

      {/* ─── Trigger ─── */}
      <section>
        <SectionHeading>Trigger</SectionHeading>
        <FieldRow label="PIR sensitivity" pending={pendingFields?.has('desired_extra_settings.pir_trigger') ?? false} sent={sentFields?.has('desired_extra_settings.pir_trigger') ?? false}>
          <Select value={form.pirTrigger === UNCHANGED ? UNCHANGED : form.pirTrigger!} onValueChange={(v) => set('pirTrigger', v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['pir_trigger']))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {PIR_TRIGGER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Time lapse" hint="Take photo at fixed interval regardless of motion" pending={pendingFields?.has('desired_extra_settings.time_lapse') ?? false} sent={sentFields?.has('desired_extra_settings.time_lapse') ?? false}>
          <div className="flex gap-2">
            <Select
              value={form.timeLapseUnit}
              onValueChange={(v) => set('timeLapseUnit', v as typeof form.timeLapseUnit)}
            >
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unchanged">(no change)</SelectItem>
                <SelectItem value="off">Off</SelectItem>
                <SelectItem value="minutes">Every N min</SelectItem>
                <SelectItem value="hours">Every N hr</SelectItem>
              </SelectContent>
            </Select>
            {(form.timeLapseUnit === 'minutes' || form.timeLapseUnit === 'hours') && (
              <Select value={String(form.timeLapseValue)} onValueChange={(v) => set('timeLapseValue', Number(v))}>
                <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(form.timeLapseUnit === 'minutes' ? TIME_LAPSE_MIN_OPTIONS : TIME_LAPSE_HR_OPTIONS).map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </FieldRow>
      </section>

      {/* ─── Schedule ─── */}
      <section>
        <SectionHeading>Schedule</SectionHeading>
        <FieldRow label="Work hour" hint="When the camera is active each day" pending={pendingFields?.has('desired_extra_settings.work_hour') ?? false} sent={sentFields?.has('desired_extra_settings.work_hour') ?? false}>
          <div className="flex flex-col gap-2">
            <Select value={form.workHourMode} onValueChange={(v) => set('workHourMode', v as typeof form.workHourMode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unchanged">(no change)</SelectItem>
                <SelectItem value="always">Always on</SelectItem>
                <SelectItem value="window">Time window</SelectItem>
              </SelectContent>
            </Select>
            {form.workHourMode === 'window' && (
              <div className="flex gap-1.5 items-center">
                <Input type="time" value={form.workHourFrom} onChange={(e) => set('workHourFrom', e.target.value)} className="h-8" />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="time" value={form.workHourTo} onChange={(e) => set('workHourTo', e.target.value)} className="h-8" />
              </div>
            )}
          </div>
        </FieldRow>

        <FieldRow label="Work days" hint="Sunday-first: leftmost bit is Sunday." pending={pendingFields?.has('desired_extra_settings.work_day') ?? false} sent={sentFields?.has('desired_extra_settings.work_day') ?? false}>
          <div className="flex gap-1 flex-wrap justify-end">
            {DAY_LABELS.map((lbl, i) => (
              <button
                key={i}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (form.workDay === null) {
                    const arr = [false, false, false, false, false, false, false]
                    arr[i] = true
                    set('workDay', arr)
                  } else {
                    const arr = [...form.workDay]
                    arr[i] = !arr[i]
                    set('workDay', arr)
                  }
                }}
                className={`h-7 w-9 rounded text-xs font-medium border ${
                  form.workDay && form.workDay[i] ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-muted-foreground'
                }`}
              >
                {lbl}
              </button>
            ))}
            {form.workDay !== null && (
              <button type="button" disabled={disabled} onClick={() => set('workDay', null)} className="h-7 px-2 rounded text-xs border border-border text-muted-foreground">
                Clear
              </button>
            )}
          </div>
        </FieldRow>
      </section>

      {/* ─── Storage ─── */}
      <section>
        <SectionHeading>Storage</SectionHeading>
        <FieldRow label="Attachment size" hint="JPEG quality — applies to photo upload" pending={pendingFields?.has('desired_extra_settings.attachment_size') ?? false} sent={sentFields?.has('desired_extra_settings.attachment_size') ?? false}>
          <Select value={form.attachmentSize === UNCHANGED ? UNCHANGED : form.attachmentSize!} onValueChange={(v) => set('attachmentSize', v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['attachment_size']))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ATTACHMENT_SIZE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Recycle storage" hint="Overwrite oldest media when SD fills" pending={pendingFields?.has('desired_extra_settings.recycle_storage_state') ?? false} sent={sentFields?.has('desired_extra_settings.recycle_storage_state') ?? false}>
          <Select value={form.recycleState === UNCHANGED ? UNCHANGED : form.recycleState!} onValueChange={(v) => set('recycleState', v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

      </section>

      {/* ─── Network ─── */}
      <section>
        <SectionHeading>Network</SectionHeading>
        <FieldRow label="Send mode" locked={lockPlumbing} hint="Instant or daily batch; off disables uploads" pending={pendingFields?.has('desired_extra_settings.send_mode') ?? false} sent={sentFields?.has('desired_extra_settings.send_mode') ?? false}>
          <Select
            value={form.sendMode === UNCHANGED ? UNCHANGED : form.sendMode.mode}
            onValueChange={(v) => {
              if (v === UNCHANGED) set('sendMode', UNCHANGED)
              else if (v === 'off') set('sendMode', { mode: 'off' })
              else set('sendMode', { mode: v as 'instant' | 'daily', count: 100 })
            }}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              <SelectItem value="instant">Instant</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="off">Off</SelectItem>
            </SelectContent>
          </Select>
          {sendModeObj && (
            <Input
              type="number" min={1} max={100}
              value={sendModeObj.count}
              onChange={(e) =>
                set('sendMode', { mode: sendModeObj.mode, count: Number(e.target.value) })
              }
              className="h-8 mt-1.5"
              placeholder="Send max num (1-100)"
            />
          )}
        </FieldRow>

        <FieldRow label="SMS control" locked={lockPlumbing} hint="GSM modem stays on to accept SMS commands" pending={pendingFields?.has('desired_extra_settings.sms_control') ?? false} sent={sentFields?.has('desired_extra_settings.sms_control') ?? false}>
          <Select value={form.smsControl === UNCHANGED ? UNCHANGED : form.smsControl!} onValueChange={(v) => set('smsControl', v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="GPS" hint="AutoGPS — camera acquires location on capture" pending={pendingFields?.has('desired_extra_settings.gps') ?? false} sent={sentFields?.has('desired_extra_settings.gps') ?? false}>
          <Select value={form.gps === UNCHANGED ? UNCHANGED : form.gps!} onValueChange={(v) => set('gps', v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Auto SIM" locked={lockPlumbing} hint="Automatic vs manual SIM selection" pending={pendingFields?.has('desired_extra_settings.auto_sim') ?? false} sent={sentFields?.has('desired_extra_settings.auto_sim') ?? false}>
          <Select value={form.autoSim === UNCHANGED ? UNCHANGED : form.autoSim!} onValueChange={(v) => set('autoSim', v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['auto_sim']))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {AUTO_SIM_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Network lock" locked={lockPlumbing} hint="Force a cellular generation" pending={pendingFields?.has('desired_extra_settings.lock_net') ?? false} sent={sentFields?.has('desired_extra_settings.lock_net') ?? false}>
          <Select value={form.lockNet === UNCHANGED ? UNCHANGED : form.lockNet!} onValueChange={(v) => set('lockNet', v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['lock_net']))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {LOCK_NET_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Heartbeat" locked={lockPlumbing} hint="Status check-in interval" pending={pendingFields?.has('desired_extra_settings.heartbeat') ?? false} sent={sentFields?.has('desired_extra_settings.heartbeat') ?? false}>
          <Select value={form.heartbeat === UNCHANGED ? UNCHANGED : String(form.heartbeat)} onValueChange={(v) => set('heartbeat', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {HEARTBEAT_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="SMS time delay" locked={lockPlumbing} hint="0-4" pending={pendingFields?.has('desired_extra_settings.sms_time_delay') ?? false} sent={sentFields?.has('desired_extra_settings.sms_time_delay') ?? false}>
          <Select value={form.smsTimeDelay === UNCHANGED ? UNCHANGED : String(form.smsTimeDelay)} onValueChange={(v) => set('smsTimeDelay', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(0, 4).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
      </section>

      {/* ─── Deterrent ─── */}
      <section>
        <SectionHeading>Deterrent</SectionHeading>
        <FieldRow label="Alarm by motion" pending={pendingFields?.has('desired_extra_settings.alarm_by_motion') ?? false} sent={sentFields?.has('desired_extra_settings.alarm_by_motion') ?? false}>
          <Select value={form.alarmByMotion === UNCHANGED ? UNCHANGED : form.alarmByMotion!} onValueChange={(v) => set('alarmByMotion', v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Alarm by cloud" pending={pendingFields?.has('desired_extra_settings.alarm_by_cloud') ?? false} sent={sentFields?.has('desired_extra_settings.alarm_by_cloud') ?? false}>
          <Select value={form.alarmByCloud === UNCHANGED ? UNCHANGED : form.alarmByCloud!} onValueChange={(v) => set('alarmByCloud', v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Alarm by AI" hint="0 = off, 1-10 = alarm seconds" pending={pendingFields?.has('desired_extra_settings.alarm_by_ai') ?? false} sent={sentFields?.has('desired_extra_settings.alarm_by_ai') ?? false}>
          <Select value={form.alarmByAi === UNCHANGED ? UNCHANGED : String(form.alarmByAi)} onValueChange={(v) => set('alarmByAi', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(0, 10).map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? 'Off' : `${n}s`}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Alarm sound" hint="0-20 seconds" pending={pendingFields?.has('desired_extra_settings.alarm_sound') ?? false} sent={sentFields?.has('desired_extra_settings.alarm_sound') ?? false}>
          <Select value={form.alarmSound === UNCHANGED ? UNCHANGED : String(form.alarmSound)} onValueChange={(v) => set('alarmSound', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(0, 20).map((n) => <SelectItem key={n} value={String(n)}>{n}s</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Alarm light" hint="0-20 seconds" pending={pendingFields?.has('desired_extra_settings.alarm_light') ?? false} sent={sentFields?.has('desired_extra_settings.alarm_light') ?? false}>
          <Select value={form.alarmLight === UNCHANGED ? UNCHANGED : String(form.alarmLight)} onValueChange={(v) => set('alarmLight', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(0, 20).map((n) => <SelectItem key={n} value={String(n)}>{n}s</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Alarm sound volume" hint="0-2" pending={pendingFields?.has('desired_extra_settings.alarm_sound_volume') ?? false} sent={sentFields?.has('desired_extra_settings.alarm_sound_volume') ?? false}>
          <Select value={form.alarmSoundVolume === UNCHANGED ? UNCHANGED : String(form.alarmSoundVolume)} onValueChange={(v) => set('alarmSoundVolume', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(0, 2).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Alarm flash" hint="Deterrent flash pattern" pending={pendingFields?.has('desired_extra_settings.alarm_flash') ?? false} sent={sentFields?.has('desired_extra_settings.alarm_flash') ?? false}>
          <Select value={form.alarmFlash === UNCHANGED ? UNCHANGED : form.alarmFlash!} onValueChange={(v) => set('alarmFlash', v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['alarm_flash']))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ALARM_FLASH_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
      </section>

      {/* ─── Regional ─── */}
      <section>
        <SectionHeading>Regional</SectionHeading>
        <FieldRow label="Language" pending={pendingFields?.has('desired_extra_settings.language') ?? false} sent={sentFields?.has('desired_extra_settings.language') ?? false}>
          <Select value={form.language === UNCHANGED ? UNCHANGED : form.language!} onValueChange={(v) => set('language', v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['language']))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {LANGUAGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Date format" pending={pendingFields?.has('desired_extra_settings.date_type') ?? false} sent={sentFields?.has('desired_extra_settings.date_type') ?? false}>
          <Select value={form.dateType === UNCHANGED ? UNCHANGED : form.dateType!} onValueChange={(v) => set('dateType', v === UNCHANGED ? UNCHANGED : (v as DesiredExtraSettings['date_type']))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {DATE_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Time zone" hint="Hours added to the UK time our server sends. UK cameras: 0" pending={pendingFields?.has('desired_extra_settings.time_zone') ?? false} sent={sentFields?.has('desired_extra_settings.time_zone') ?? false}>
          <Select value={form.timeZone === UNCHANGED ? UNCHANGED : String(form.timeZone)} onValueChange={(v) => set('timeZone', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(-12, 12).map((n) => <SelectItem key={n} value={String(n)}>{n > 0 ? `+${n}` : n}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Time zone offset" hint="Extra minutes (-60..60)" pending={pendingFields?.has('desired_extra_settings.time_zone_offset') ?? false} sent={sentFields?.has('desired_extra_settings.time_zone_offset') ?? false}>
          <Select value={form.timeZoneOffset === UNCHANGED ? UNCHANGED : String(form.timeZoneOffset)} onValueChange={(v) => set('timeZoneOffset', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(-60, 60).map((n) => <SelectItem key={n} value={String(n)}>{n > 0 ? `+${n}` : n}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
      </section>

      {/* ─── System ─── */}
      <section>
        <SectionHeading>System</SectionHeading>
        <FieldRow label="Photo burst" hint="Photos per PIR trigger" pending={pendingFields?.has('desired_extra_settings.photo_burst') ?? false} sent={sentFields?.has('desired_extra_settings.photo_burst') ?? false}>
          <Select value={form.photoBurst === UNCHANGED ? UNCHANGED : String(form.photoBurst)} onValueChange={(v) => set('photoBurst', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(1, 3).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Beep sound" pending={pendingFields?.has('desired_extra_settings.beep_sound') ?? false} sent={sentFields?.has('desired_extra_settings.beep_sound') ?? false}>
          <Select value={form.beepSound === UNCHANGED ? UNCHANGED : form.beepSound!} onValueChange={(v) => set('beepSound', v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Timestamp on media" pending={pendingFields?.has('desired_extra_settings.time_stamp') ?? false} sent={sentFields?.has('desired_extra_settings.time_stamp') ?? false}>
          <Select value={form.timeStamp === UNCHANGED ? UNCHANGED : form.timeStamp!} onValueChange={(v) => set('timeStamp', v === UNCHANGED ? UNCHANGED : (v as 'on' | 'off'))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {ON_OFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="UK attach size" hint="0-9 (UK-region build)" pending={pendingFields?.has('desired_extra_settings.uk_attach_size') ?? false} sent={sentFields?.has('desired_extra_settings.uk_attach_size') ?? false}>
          <Select value={form.ukAttachSize === UNCHANGED ? UNCHANGED : String(form.ukAttachSize)} onValueChange={(v) => set('ukAttachSize', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(0, 9).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="UK lock menu" locked={lockPlumbing} hint="0 = off, 1 = lock camera menu" pending={pendingFields?.has('desired_extra_settings.uk_lock_menu') ?? false} sent={sentFields?.has('desired_extra_settings.uk_lock_menu') ?? false}>
          <Select value={form.ukLockMenu === UNCHANGED ? UNCHANGED : String(form.ukLockMenu)} onValueChange={(v) => set('ukLockMenu', v === UNCHANGED ? UNCHANGED : Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCHANGED}>(no change)</SelectItem>
              {intRange(0, 1).map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? 'Off' : 'Lock'}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
      </section>
    </LockedContext.Provider>
  )
}
